/**
 * problemApi — 문제 리스트·검색(②①) + 문제 상세 조회(③) + 문제 편집(B-1) + 이미지→SVG(B-5).
 *
 * 계약: docs/exam-builder-v2/BE-20260825-problem-hub-integrated.md §2·§3 ·
 *       BE-20260825-fe-handover.md §B-1·§B-5·§D · BE-20260825-reply-batch.md §3-1·§3-2 ·
 *       BE-20260825-contract-answers.md §2-1 ·
 *       **BE-20260826-audit-fixes-folder-tree-v2apis.md §1-5(검색)·§3(문제/problem-hub)·§5(figspec)**
 *       (전부 dev 배포·검증 완료).
 * 봉투 { status, message, status_code, data } — 성공 판정 status === 'success'. 409 는 data.reason.
 *
 * 검색은 exam-papers 네임스페이스에 있지만 범용이다(시험지 문항 담기와 이 허브가 공용).
 * 필터를 하나도 주지 않으면 전체 목록이 된다 — "리스트"와 "검색"이 같은 엔드포인트다.
 *
 * 권한(계약 §3): `/api/v2/problems` 는 **목록·상세 둘 다 manager_only** 다 —
 * 비매니저 토큰은 **403**, 무효·만료 토큰은 **401**. 두 코드는 화면이 서로 다른 말을 해야 한다
 * (403 은 다시 로그인해도 소용없고, 401 은 로그인하면 풀린다). 목록(`GET /api/v2/problems`)을
 * 이 저장소에서 부르는 곳은 이제 없다(검증 하네스를 2026-08-28 에 걷어냈다) — 여기서는 상세만 쓴다.
 */
import { fetchWithTokenRefresh, fetchAPI, AUTH_REDIRECT } from '../../utils/apiUtils';
// 변환 모드는 그림 쪽과 **같은 한 벌**을 쓴다(계약 §9-4 가 두 경로 공통이라 적었다) —
// 사본을 두면 서버가 값을 늘릴 때 한쪽만 낡는다.
import { figureModePayload } from '../FigureEditor/figureApi';
import { labelSlug } from './labelEdit';

// 401 표식은 공용 자리(utils/apiUtils)에 하나만 둔다 — 여기서는 그대로 다시 내보낸다.
// 모듈마다 Symbol 을 새로 만들면 다른 모듈이 돌려준 값과의 비교가 영원히 false 다.
export { AUTH_REDIRECT };

function unwrap(res, fallbackMsg) {
  if (!res || res.status !== 'success') {
    throw new Error(res?.message || fallbackMsg);
  }
  return res.data;
}

/**
 * 상태코드 분기가 필요한 쓰기 공용 — 실패 시 Error.httpStatus + Error.data 를 실어 throw.
 * (analysisApi·variationApi 의 같은 이름 함수와 동형. 그쪽도 미수출이라 여기 로컬로 둔다.)
 * 편집의 낙관락 409 처럼 "무엇이 왜 막혔나"가 봉투 message·data 에만 있는 응답이 있어,
 * 상태코드를 삼키는 fetchWithTokenRefresh 로는 화면이 분기할 수 없다.
 */
async function requestJsonWithStatus(path, { method = 'POST', payload } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let response;
  try {
    response = await fetchAPI(path, {
      method,
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    });
  } catch (netErr) {
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 아래에서 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    const err = new Error(body?.message || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.data = body?.data ?? null; // 409(낙관락)·400 의 기계 판별 코드를 화면까지 나른다
    // 표준 봉투였는가 — 배포 전 서버는 경로가 없어 봉투 없는 404·405 를 준다(검색의 「모두 포함」이 이것으로 배포 전을 가린다)
    err.envelope = body != null && typeof body.status === 'string';
    throw err;
  }
  return body.data;
}

/**
 * 상태코드 분기가 필요한 **읽기(GET)** 공용 — 실패 시 Error.httpStatus + Error.data 를 실어 throw.
 *
 * 공용 fetchWithTokenRefresh 를 쓰지 않는 이유는 하나다: 그쪽은 400 을 "잘못된 요청입니다.
 * 입력 정보를 확인해주세요.", 403 을 "접근 권한이 없습니다."라는 **고정 문구로 갈아 끼우고
 * 상태코드와 서버 message 를 둘 다 버린다**(utils/apiUtils.js). 계약 §1-5 의 400
 * (concept_q 매칭 개념 200개 초과 — 서버가 "검색어를 더 구체적으로"라고 말해 준다)과 §3 의 403
 * (manager_only)은 화면이 서로 다른 안내를 해야 하는 갈래라, 이 두 정보를 잃으면 분기할 수 없다.
 *
 * 실패 시의 **모양은 fetchWithTokenRefresh 와 같게 유지한다**(성공=data, 실패=throw). 이 모듈의
 * 검색 함수는 ExamPapersV2(ProblemPickerModal·PaperItemsEditor)도 쓰는데, 그쪽 계약을 바꾸지
 * 않으려면 반환 형태가 같아야 한다. 401 에서 AUTH_REDIRECT 를 돌려주지 않고 throw 하는 것도
 * 같은 이유다 — 심볼을 돌려주면 그 화면들이 목록인 줄 알고 그대로 렌더한다.
 */
async function getJsonWithStatus(path, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    throw new Error(fallbackMsg);
  }
  let response;
  try {
    response = await fetchAPI(path, {
      method: 'GET',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
    });
  } catch (netErr) {
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    const err = new Error(fallbackMsg);
    err.httpStatus = 401;
    throw err;
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 아래에서 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    const err = new Error(body?.message || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.data = body?.data ?? null;
    throw err;
  }
  return body.data;
}

// 난이도 1~5 — 숫자만 보이면 방향(쉬움/어려움)을 알 수 없다. 레거시(WorkDetail)와 같은 문구를 쓴다.
export const DIFFICULTY_LABEL = {
  1: '매우 쉬움', 2: '쉬움', 3: '보통', 4: '어려움', 5: '매우 어려움',
};
export const difficultyOptionLabel = (n) => `${n} (${DIFFICULTY_LABEL[n]})`;

/**
 * 난이도 배지의 **색만** — 문제관리(`/problem-edit`)의 원형 배지에서 옮겨 온 값이다
 * (오너 지시 2026-08-29 「원형 배지 디자인 및 색상과 동일하게 — 값 아니고 디자인만」).
 * 노랑→초록→파랑→주황→빨강으로, 쉬움에서 어려움으로 간다. 채움 100 / 글자 600.
 *
 * **모양은 여기서 정하지 않는다** — 쓰는 쪽이 원(표)으로도 알약(카드)으로도 두른다.
 * 색을 한 곳에 둔 이유는 같은 난이도가 화면마다 다른 색으로 보이지 않게 하기 위해서다:
 * 이 배지는 지금 다섯 화면에 한 글자도 다르지 않게 복붙돼 있었다.
 */
const DIFFICULTY_BADGE_CLASS = {
  1: 'bg-yellow-100 text-yellow-600',
  2: 'bg-green-100 text-green-600',
  3: 'bg-blue-100 text-blue-600',
  4: 'bg-orange-100 text-orange-600',
  5: 'bg-red-100 text-red-600',
};
export const difficultyBadgeClass = (n) => DIFFICULTY_BADGE_CLASS[n] || 'bg-gray-100 text-gray-600';

/**
 * 문제 번호의 색과 툴팁 — **번호가 곧 상태다**(오너 지시 2026-08-28 로 「비어 있는 것」 칼럼을
 * 빼고 번호로 옮겼다). 완비면 브랜드 초록, 아직 채울 것이 남았으면 먹색. 굵기는 둘 다 bold.
 *
 * **색만으로 말하지 않는다** — 무엇이 비었는지는 title 이 이름을 붙여 준다. 색을 못 보는
 * 사람에게 색은 정보가 아니고, 칼럼이 사라진 자리를 대신할 것이 title 뿐이다.
 * 표와 카드가 같은 규칙을 쓰도록 여기 둔다(2026-08-29) — 한쪽만 고치면 같은 문제가 두 얼굴이 된다.
 */
export function problemIdTone(item) {
  const needs = getNeedBadges(item);
  return {
    className: needs.length === 0 ? 'font-bold text-primary' : 'font-bold text-gray-900',
    title: needs.length === 0
      ? '완비 — 개념·난이도·정답·풀이가 모두 있습니다'
      : `아직 비어 있음: ${needs.map((n) => n.label).join(' · ')}`,
  };
}

/**
 * 출신(origin_type) — 목록 배지. 100 사람 / 200 AI / 300 OCR.
 *
 * **900 은 「미판정」이 아니다** — 계약 §6 F1 에 따르면 900 은 *배포 이전 레거시 사람 문제*이고,
 * 사람 100 은 배포 이후 신규 생성분부터 붙는다. 그래서 900 은 나중에 채워질 빈칸이 아니라
 * 이미 뜻이 정해진 다수 집단이다.
 *
 * 그럼에도 900 행을 여기 두지 않는 이유: dev 백필 실측이 200=426 · 300=221 · 900=60,357 로
 * **전체의 98.94% 가 900** 이라, 배지를 그리면 목록 거의 전부가 같은 회색 칩 하나로 덮여
 * 배지가 아무것도 가르지 못한다. 대비도 미달이었다(text-gray-400 대 bg-gray-100 = 2.31:1,
 * 저장소 하한 4.5:1). 900 을 빼면 getOriginDisplay 가 null 을 돌려주고, 렌더 5곳이 이미
 * `origin ? … : —` 로 방어돼 있어 화면은 F1 이전(출신을 아는 문제만 배지)으로 돌아간다.
 * 되돌리려면 위 비율부터 다시 재라 — 900 이 줄어든 뒤에야 배지가 뜻을 갖는다.
 */
export const ORIGIN_META = {
  100: { label: '사람', cls: 'bg-gray-100 text-gray-600' },
  200: { label: 'AI', cls: 'bg-violet-100 text-violet-700' },
  300: { label: 'OCR 등록', cls: 'bg-sky-100 text-sky-700' },
};

// ai_kind — AI 문제의 갈래. generated = 무에서 생성(원본 없음) / variation = 변형(원본 있음)
export const AI_KIND_LABEL = { generated: '생성', variation: '변형' };

/**
 * 목록 배지 텍스트 — "AI·변형"처럼 출신과 갈래를 한 칩에 담는다(계약 §2 권고)
 *
 * 백엔드의 `origin_label` 보다 **위 표의 라벨을 먼저 쓴다.** 배지에 쓰는 말은 화면의 말이고,
 * 백엔드는 자기 쪽 절차 이름을 보낸다 — 300 이 오면 origin_label 이 「OCR 승격」인데
 * 「승격」은 OCR(/ocr-hub) 안에서 문제은행으로 올리는 *절차* 이름이지, 문제은행 목록에서
 * 「이 문제가 어디서 왔는가」를 읽는 사람의 말이 아니다. 그래서 「OCR 등록」으로 부른다.
 * origin_label 은 표에 없는 출신이 새로 생겼을 때의 폴백으로만 남긴다.
 */
export function getOriginDisplay(item) {
  if (!item) return null;
  const meta = ORIGIN_META[item.origin_type];
  const base = meta?.label || item.origin_label;
  if (!base) return null;
  const kind = AI_KIND_LABEL[item.ai_kind];
  return { text: kind ? `${base}·${kind}` : base, cls: meta?.cls || 'bg-gray-100 text-gray-600' };
}

/**
 * 검색 결과의 최근 분석 상태(reply-batch §3-1) — needs_analysis=true 로 부를 때만 실려 온다.
 * 100 대기 / 150 처리중 / 200 완료(=분석 검수 대기) / 500 실패 / null 분석 이력 없음.
 * ⚠ 110(큐 대기)은 여기 없다 — 그건 OCR 스테이징 M4 값이고, 은행 분석(ProblemAnalysis v2)엔
 *   나오지 않는다(reply-batch §3-1 이 handover 의 110 표기를 정정). 넣어 두면 있지도 않은
 *   상태를 폴링 종료 조건에서 기다리게 된다.
 * `null` 칸: 속성 접근이 키를 문자열로 바꾸므로 ANALYSIS_STATUS[null] 이 이 칸을 집는다.
 * (undefined 는 안 집히니 표시는 getAnalysisStatusDisplay 를 거치는 편이 안전하다.)
 */
export const ANALYSIS_STATUS = {
  100: { label: '분석 대기', cls: 'bg-gray-100 text-gray-800' },
  150: { label: '분석 중', cls: 'bg-blue-100 text-blue-800' },
  200: { label: '분석 완료', cls: 'bg-green-100 text-green-800' },
  500: { label: '분석 실패', cls: 'bg-red-100 text-red-800' },
  null: { label: '분석 전', cls: 'bg-gray-100 text-gray-400' },
};

/** 폴링을 계속해야 하는 상태(분석 요청 후 목록 재조회 — reply-batch §3-1) */
export const ANALYSIS_POLLING_STATUSES = [100, 150];

/** 배지 한 칸 — 형제 헬퍼들과 같은 { text, cls } 로 맞춘다(ocrApi.getAnalysisStatusDisplay 관례) */
export function getAnalysisStatusDisplay(status) {
  const meta = status == null ? ANALYSIS_STATUS.null : ANALYSIS_STATUS[status];
  return { text: meta?.label || `분석 ${status}`, cls: meta?.cls || 'bg-gray-100 text-gray-800' };
}

/**
 * need_* 배지 4개 — "이 문제에 무엇이 비었나". 검색 item 과 옛 candidates 가 같은 키를 준다.
 * 옛 분석 대상 패널(은퇴) 의 정의를 그대로 옮겨 왔다(그 패널은 검색 통합으로 은퇴).
 * ⚠ 순서에 뜻이 있다: 앞의 둘(개념·난이도)만 needs_analysis 필터에 걸린다 — §3-2.
 * ⚠ need_concept 의 뜻이 2026-08-26 에 넓어졌다(계약 §1-5): 개념 칸이 **`concept_ids:[]`
 *   빈 배열이어도 "개념 필요"로 센다.** 이전에는 빈 배열을 "개념 있음"으로 봐서, 분석 화면은
 *   "개념 없음"이라는데 이 목록은 배지도 안 띄우고 needs_analysis 필터에도 안 걸리는 어긋난
 *   화면이 나왔다. 두 화면이 이제 같은 정의를 쓴다 — 그래서 필터를 켰을 때 건수가 늘 수 있다.
 */
export const NEED_META = [
  { key: 'need_concept', label: '개념', cls: 'bg-amber-50 text-amber-700' },
  { key: 'need_difficulty', label: '난이도', cls: 'bg-amber-50 text-amber-700' },
  { key: 'need_answer', label: '정답', cls: 'bg-amber-50 text-amber-700' },
  { key: 'need_solution', label: '풀이', cls: 'bg-amber-50 text-amber-700' },
];

/**
 * 상세 응답(`GET /api/v2/problems/{id}`)을 need_* 플래그 꼴로 바꾼다.
 *
 * **상세에는 need_* 가 없다** — 그 플래그는 검색 응답(problem-search)에만 실린다. 그래서 상세를
 * 그대로 getNeedBadges 에 넘기면 빈 배열이 나오고, 화면은 그것을 **「완비」로 읽는다.** 아무것도
 * 비어 있지 않다고 초록으로 말하게 되는 것이라, 파생을 거치지 않고는 번호에 색을 입힐 수 없다.
 *
 * 규칙은 서버와 같은 것을 쓴다 — 특히 개념은 **`concept_ids` 가 빈 배열이어도 「필요」**다
 * (계약 §1-5, 2026-08-26 로 넓어진 정의. 이전에는 빈 배열을 「있음」으로 봐서 분석 화면과
 *  목록이 어긋났다). 풀이는 has_solution 과 solution_lexical 둘 다 없을 때만 「필요」로 센다 —
 * 상세 응답이 둘 중 하나만 채워 오는 경우가 있다.
 */
export function deriveNeeds(detail) {
  if (!detail) return null;
  const conceptIds = Array.isArray(detail.concept_ids) ? detail.concept_ids : [];
  const answer = detail.answer == null ? '' : String(detail.answer).trim();
  return {
    need_concept: conceptIds.length === 0,
    need_difficulty: detail.difficulty == null,
    need_answer: answer === '',
    need_solution: !detail.has_solution && detail.solution_lexical == null,
  };
}

/** 켜져 있는 need_* 만 골라 준다(없으면 빈 배열 — "완비" 문구는 화면이 정한다) */
export function getNeedBadges(item) {
  if (!item) return [];
  return NEED_META.filter((m) => item[m.key]);
}

/**
 * 개념명 정규화 — 이 계열 API가 concept_names 를 세 형태로 준다:
 *   상세(§3) [{concept_id,name}] · 분석 상세 {"425":"마름모"} 맵 · 검색(§2) [{concept_id,name}]
 * 어느 쪽이 와도 이름 문자열 배열로 만든다. 한 형태로 단정하면 "[object Object]" 가 목록 전
 * 행에 노출되거나(객체 배열) 개념이 조용히 사라진다(맵).
 * ※ contract-answers §2-1: 검색·생성 상세의 concept_names 는 [{concept_id,name}] 로 **확정**됐다.
 *   그래도 맵·문자열 흡수는 남긴다 — 분석(④) current 는 레거시 맵을 유지한다고 못박았고(§2-2),
 *   확정 전에 저장된 응답을 캐시로 되읽는 경로가 있다.
 */
export function normalizeConceptNames(v) {
  if (!v) return [];
  if (typeof v === 'string') return v.trim() ? [v.trim()] : [];
  if (Array.isArray(v)) {
    return v.map((c) => (typeof c === 'string' ? c : c?.name ?? c?.text ?? null)).filter(Boolean);
  }
  if (typeof v === 'object') return Object.values(v).filter((x) => typeof x === 'string');
  return [];
}

/**
 * 문제 검색·목록 — GET /api/v2/exam-papers/problem-search
 * 전부 선택 파라미터. 빈 값은 보내지 않는다(빈 문자열을 보내면 서버가 조건으로 받는다).
 * status=999(본문 확정) 문제만 나온다.
 *
 * needs_analysis(§D · reply-batch §3-2) = **개념 또는 난이도가 없는 확정 문제**
 * (= 옛 candidates 의 '미처리'와 같은 모집단). 정답·풀이만 없는 문제는 이 필터에 안 걸린다 —
 * need_answer/need_solution 배지로는 보이지만 필터 대상이 아니다. 그런 것까지 걸러 보려면
 * has_answer=false·has_solution=false 를 조합한다. 화면 문구는 "개념·난이도가 비어 있는 문제만".
 *
 * 응답 item(가공하지 않고 그대로 넘긴다):
 *   problem_id·preview·labels·concept_ids·concept_names·difficulty·author·
 *   origin_type·origin_label·ai_kind · need_concept/need_difficulty/need_answer/need_solution ·
 *   그리고 needs_analysis=true 일 때만 analysis_status·analysis_updated_at
 *   (안 켜면 두 필드는 조인 skip 이라 항상 null 이다 — "분석 전"으로 오독하지 말 것).
 *
 * 2026-08-26 계약 §1-5 로 바뀐 것 넷:
 *  - **concept_q 매칭 개념이 200개를 넘으면 400** 이다("검색어를 더 구체적으로"). 이전에는
 *    무순서로 조용히 잘라서 결과도 total 도 말없이 빠졌다 — 화면이 400 을 잡아 안내해야 한다.
 *    Error.httpStatus === 400 으로 갈린다(getJsonWithStatus 가 상태코드를 살려 준다).
 *  - `labels`·`author`·`concept_q` 안의 `%`·`_` 는 **리터럴**이다. 와일드카드로 새지 않으니
 *    사용자가 친 그대로 보내면 된다(여기서 이스케이프하지 않는다).
 *  - need_concept 배지·needs_analysis 필터가 `concept_ids:[]` 빈 배열도 "개념 필요"로 센다.
 *  - `analysis_updated_at` 은 **분석 낙관락 토큰**이라 존(Z) 없는 종전 포맷 그대로다(§1-1 예외) —
 *    시각으로 포맷해 보여 주는 값이 아니라 expected_updated_at 에 되돌려 넣는 값이다.
 *
 * 2026-09-13 — conceptMatch:'all'(개념 AND · 모두 포함). 주소줄의 concept_ids 는 여전히 「하나라도」뿐이라
 * 「모두」는 같은 주소의 **본문 검색(POST)** 으로 나간다(searchConceptAll). 반환 모양은 같고 warnings 만 더해진다.
 */
export async function searchProblems(filters = {}, getAccessToken) {
  const {
    conceptIds, conceptMatch = 'any', conceptQ, labels, difficulty, author,
    hasAnswer, hasSolution, problemIds, originType, aiKind, needsAnalysis,
    withBody = false,
    page = 1, perPage = 20,
  } = filters;
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  const put = (key, v) => {
    if (v == null) return;
    const s = Array.isArray(v) ? v.join(',') : String(v).trim();
    if (s !== '') params.append(key, s);
  };
  put('concept_ids', conceptIds);
  put('concept_q', conceptQ);
  put('labels', labels);
  put('difficulty', difficulty);
  put('author', author);
  put('problem_ids', problemIds);
  put('origin_type', originType);
  put('ai_kind', aiKind);
  if (hasAnswer != null) params.append('has_answer', hasAnswer ? 'true' : 'false');
  if (hasSolution != null) params.append('has_solution', hasSolution ? 'true' : 'false');
  // 계약에 있는 값은 true 뿐이다 — 끌 때는 파라미터 자체를 빼야 한다(needs_analysis=false 는 미정의).
  if (needsAnalysis) params.append('needs_analysis', 'true');
  // with_body=1 — 각 item 에 조판 본문(body_lexical)을 **동봉**받는 스위치(계약 §9).
  // **응답이 커진다**: 한 페이지 20건이면 본문 20벌이 함께 온다. 그럼에도 이 길인 이유는
  // 계약 §9 가 설계한 「별도 왕복 없음」이다 — 같은 엔드포인트를 problem_ids 로 한 번 더
  // 부르면 페이지를 넘길 때마다 왕복이 1회씩 더 난다. 기본은 꺼짐(응답 크기 유지).
  if (withBody) params.append('with_body', '1');

  // 개념 AND(모두 포함)만 본문 검색으로 옮긴다. 그 밖의 검색은 **종전 주소줄 그대로**다 — 본문 검색에는
  // 5초 상한(408)이 새로 붙어, 전체 목록 같은 무거운 검색까지 옮기면 없던 실패가 생긴다.
  // 개념이 하나면 「모두」와 「하나라도」의 결과가 같아 주소줄로 둔다(conceptAllExpr 가 null).
  const allExpr = conceptMatch === 'all' ? conceptAllExpr(conceptIds) : null;
  const d = (allExpr
    ? await searchConceptAll(allExpr, params, getAccessToken)
    : await getJsonWithStatus(
      `/api/v2/exam-papers/problem-search?${params}`,
      getAccessToken,
      SEARCH_FAIL_MSG,
    )) || {};
  // 목록 응답 형태 방어(items 별칭·평면 배열) — ocrApi.fetchProjectProblems 관례
  const items = Array.isArray(d) ? d
    : Array.isArray(d.items) ? d.items
      : Array.isArray(d.list) ? d.list : [];
  return {
    items,
    page: d.page ?? page,
    perPage: d.per_page ?? perPage,
    totalItems: d.total_items ?? items.length,
    totalPages: d.total_pages ?? 1,
    // 본문 검색만 싣는다 — 서버에 없는 개념 [{group, term, unknown_values}]. 「모두」에서는 그 조건이 거짓이 된다
    warnings: Array.isArray(d.warnings) ? d.warnings : [],
  };
}

const SEARCH_FAIL_MSG = '문제 목록을 불러오지 못했습니다.';

/** 본문 검색이 배포 전인 서버 — searchProblems 가 이 사유(err.reason)로 갈아 끼워 던진다(운영은 BE 배포 대기, 2026-09-13) */
export const CONCEPT_ALL_UNAVAILABLE = 'concept_all_unavailable';
/** 그때 화면에 쓰는 말 — 조립 창(본문 검색을 syllabusApi 로 직접 부른다)도 같은 문장을 쓴다 */
export const CONCEPT_ALL_UNAVAILABLE_MSG = '이 서버에는 아직 AND(모두 포함) 검색이 없습니다 — OR 로 검색할 수 있습니다.';

/** 주소줄 키 중 본문 검색에서 **제자리가 따로 있는** 것 — 나머지는 filters 로 그대로 옮긴다 */
const BODY_OWN_KEYS = new Set(['page', 'per_page', 'with_body', 'concept_ids']);

const intList = (v) => (Array.isArray(v) ? v : String(v ?? '').split(','))
  .map((x) => String(x).trim())
  .filter((s) => /^-?\d+$/.test(s))
  .map(Number);

/**
 * 개념 「모두 포함」 식 — 고른 개념이 **둘 이상일 때만** 만든다(하나면 null — 주소줄 검색과 결과가 같다).
 * 식 모양은 강의 v2 와 같다: 묶음 하나 · 개념 항 하나(match 'all').
 * 문제(/problem-hub) 검색과 시험지 조립 창이 **같은 규칙**을 쓰려고 여기 한 곳에 둔다.
 * @param {number[]|string} conceptIds 배열 또는 콤마 문자열 — 중복은 한 번만 센다
 */
export function conceptAllExpr(conceptIds) {
  const ids = [...new Set(intList(conceptIds))];
  if (ids.length < 2) return null;
  return { version: 1, groups: [{ terms: [{ field: 'concept', match: 'all', values: ids }] }] };
}

/**
 * 개념 「모두 포함」 검색 — POST /api/v2/exam-papers/problem-search (주소줄 검색과 같은 주소, 본문으로).
 * 본문 {expr, filters, page, per_page, with_body}. filters 는 주소줄과 **같은 키·뜻**이고 식과 「그리고」로 겹친다
 * (라우트 설명문 · 강의 v2 계약 §3·S1) — 그래서 주소줄에 담던 값을 그대로 옮기고 개념만 식이 맡는다.
 * 권한은 주소줄 검색과 같다(staff_required). 분석 진행상태는 filters.needs_analysis 로 똑같이 켜진다.
 * 실패: 408(5초 상한) · 400(식 한도) — 서버 문구가 한국어라 그대로 둔다.
 *       배포 전 서버는 봉투 없는 405 라 message 가 영어로 온다 → 사유·문구를 갈아 끼운다.
 */
async function searchConceptAll(expr, params, getAccessToken) {
  const filters = {};
  params.forEach((v, k) => { if (!BODY_OWN_KEYS.has(k)) filters[k] = v; });
  const body = { expr, filters, page: Number(params.get('page')), per_page: Number(params.get('per_page')) };
  if (params.get('with_body') === '1') body.with_body = 1;
  let d;
  try {
    d = await requestJsonWithStatus('/api/v2/exam-papers/problem-search', { method: 'POST', payload: body }, getAccessToken, SEARCH_FAIL_MSG);
  } catch (e) {
    if (e?.envelope === false && (e.httpStatus === 404 || e.httpStatus === 405)) {
      e.reason = CONCEPT_ALL_UNAVAILABLE;
      e.message = CONCEPT_ALL_UNAVAILABLE_MSG;
    }
    throw e;
  }
  // 401 — 쓰기 공용은 표식을 돌려주지만, 검색은 주소줄 쪽(getJsonWithStatus)과 같이 던진다.
  // 표식을 돌려주면 부른 화면이 목록인 줄 알고 그대로 그린다.
  if (d === AUTH_REDIRECT) {
    const err = new Error(SEARCH_FAIL_MSG);
    err.httpStatus = 401;
    throw err;
  }
  return d;
}

/**
 * 문제 상세 — GET /api/v2/problems/{id}
 * data: problem_id·problem_uuid·author·status·labels_raw·origin_type·origin_label·origin_parent_id·
 *       body_lexical·answer·solution_lexical·has_solution·concept_ids·concept_names[{concept_id,name}]·
 *       difficulty·updated_at(편집 낙관락에 쓴다)·
 *       ocr_project_id·ocr_prob_id(이 문제를 만든 OCR 프로젝트·행, 늘 짝 · 아니면 null — 회신 BE-20261002, 운영 배포 전엔 칸 없음)
 *
 * 권한(계약 §3): manager_only — 비매니저 **403** / 무효 토큰 **401**. 목록도 같은 게이트로
 * 통일됐다(이전엔 목록만 user_required 라 교사가 목록은 보고 상세는 못 여는 어긋난 화면이 났다).
 * 상세는 종전과 같아 여기 코드는 바뀌지 않는다 — 화면이 두 코드를 갈라 안내하면 된다.
 */
export async function fetchProblem(problemId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/problems/${problemId}`, {}, getAccessToken);
  return unwrap(res, '문제를 불러오지 못했습니다.');
}

/**
 * 이 문제를 받은 학생 — GET /api/v2/problems/{problem_id}/solvers
 * 계약: docs/problem-hub-v2/BE-20260911-reply-solvers-by-problem.md (dev·운영 반영 5e1bd02).
 *
 * 「받은」 학생이다 — 채점 기록이 없어도 이 문제가 나간 학생은 목록에 든다(회신 §0, 오너 지시).
 * 게이트는 상세와 같은 staff_required. 교사 토큰이면 서버가 담당 학생만 돌려주고 scope:'mine' 을 싣는다
 * (범위 밖 학생은 403 이 아니라 목록에서 빠진다 — 회신 §2-3). 없는·삭제된 문제는 404 → Error.httpStatus.
 *
 * 파라미터 다섯은 늘 싣는다. 모드는 학생당 한 줄(latest)만 쓴다 — 기록당 한 줄(record)은 이 화면 밖이다.
 * 정렬 기본값은 **마지막 출제**다: 운영의 채점 시각은 2026-08-14 백필 시각에 몰려 있어서(회신 §4)
 * 서버 기본(last_activity)이나 채점 시각으로 세우면 옛 학생이 맨 위로 온다.
 *
 * 반환은 서버 data 그대로다 — { problem_id, problem_uuid, granularity, scope, summary, items, page, per_page,
 * total_items, total_pages }. 페이지 이어 받기·행 판정은 solversApi 가 한다
 * (시험지 탭의 paperUsageApi ↔ examPaperApi 와 같은 나눔).
 */
export async function fetchProblemSolvers(problemId, {
  page = 1, perPage = 100, orderBy = 'last_assigned', order = 'desc',
} = {}, getAccessToken) {
  const params = new URLSearchParams({
    granularity: 'latest',
    order_by: orderBy,
    order,
    page: String(page),
    per_page: String(perPage),
  });
  return getJsonWithStatus(
    `/api/v2/problems/${problemId}/solvers?${params}`,
    getAccessToken,
    '이 문제를 받은 학생을 불러오지 못했습니다.',
  );
}

/**
 * 문제 편집 — PATCH /api/v2/problems/{problem_id} (fe-handover §B-1)
 * patch { body_lexical?: editorState, answer?: string|null, solution_lexical?: editorState,
 *         concept_ids?: int[], difficulty?: 1~5, expected_updated_at?: ISO }  — **보낸 키만 반영**.
 *   200 → 갱신된 문제 상세(fetchProblem 동형 · concept_ids 는 서버가 order 내림차순으로 다시 세운다).
 * - concept_ids·difficulty 는 dev 279bbed 부터(회신 BE-20260913). **비우기가 없다** — null·[] 는 400.
 *   없거나 폐기된 개념도 400 인데 문구가 어느 것인지 말하지 않는다. 옛 서버는 두 키를 「허용되지 않는 필드」 400 으로 거절한다.
 *
 * - 여기서 `answer: null` 은 **"정답 비우기"** 다. 변형·생성 검수 PATCH 의 null(=수정 철회,
 *   원본 산출로 되돌림)과 뜻이 정반대이니 같은 화면에서 섞어 쓰지 말 것.
 * - 낙관락: expected_updated_at(직전 GET 의 updated_at)이 어긋나면 **409** — 화면이 "다른 사람이
 *   먼저 저장했습니다"로 분기할 수 있게 httpStatus·data 를 실어 throw 한다. 저장 성공 시 응답의
 *   새 updated_at 을 다음 편집에 쓴다.
 * - **2026-08-26 계약 §3 로 낙관락이 두 군데 세졌다.** 화면 흐름이 달라지는 변화라 옮겨 적는다:
 *   ① 409 봉투에 **`data.reason:'stale'`** 이 동봉된다(이전엔 data 가 null 이었다). 판별은
 *      message 문자열이 아니라 이 값으로 한다 — 문구는 백엔드가 언제든 다듬는다.
 *   ② **분석 승인·본문 되돌리기가 이제 `Problems.updated_at` 을 전진시킨다.** 이전에는 그 둘이
 *      Problems 행을 건드리지 않아, 열어 둔 편집기가 옛 expected_updated_at 으로 통과해
 *      **방금 승인된 정답·풀이를 조용히 되돌려 놓았다**(감사 C1 의 실제 시나리오). 이제는 409 다.
 *      즉 "승인·되돌리기 뒤에는 반드시 상세를 다시 읽어야 저장할 수 있다".
 *   ③ 밀리초 단위 동시 저장도 두 번째가 409 stale 이다(C3 — 조건부 갱신으로 바뀌었다).
 * - 빈/오형식 editorState(root.children 없음)·널만·빈 요청은 400. **`expected_updated_at` 이
 *   문자열이 아니면(숫자·객체) 400** 이다(§3 타입 가드). 여기서 미리 막지 않는다 —
 *   무엇이 왜 틀렸는지는 봉투 message 가 훨씬 정확하다.
 * - 은행 문제를 **직접** 고친다(OCR 편집기는 스테이징을 고친다). 본문만 되돌리기 이력이 남고
 *   정답·풀이는 이력이 없다.
 * - patch 는 손대지 않고 그대로 보낸다 — 키를 추려 내면 화면이 새 키를 붙였을 때 조용히 사라진다.
 */
export async function updateProblem(problemId, patch, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/problems/${problemId}`,
    { method: 'PATCH', payload: patch },
    getAccessToken,
    '문제 저장에 실패했습니다.',
  );
}

/**
 * 이미지 → SVG 변환 — POST /api/v2/problems/figspec-convert (fe-handover §B-5)
 * image: "data:image/png;base64,..." 또는 순수 base64. 그대로 보낸다(둘 다 서버가 받는다).
 * 200 → { convertible, spec, svg(data-URI), width, height, alt, issues[] }
 *
 * - **수 초 걸리는 동기 LLM 호출**이다. 버튼에 로딩 표시가 없으면 사용자가 연타한다.
 * - 실패 구분: 변환 못 하는 그림(사진·입체·수치 불명)은 500 이 아니라 **200 + convertible:false**
 *   (사유는 issues[]). 즉 catch 로 들어오는 건 진짜 오류(400·서버 장애)뿐이고, convertible:false 는
 *   정상 응답이니 "실패했습니다"가 아니라 "원본을 유지합니다"로 안내한다. 손실은 없다.
 * - **2026-08-26 계약 §5 로 그 약속이 실제로 지켜지게 됐다(E2).** 이전에는 LLM 이 이상한 spec 을
 *   돌려주면(배열 자리에 숫자, expr 에 최상위 콤마) 서버가 그대로 터져 **500** 이 왔다 —
 *   화면은 "서버 오류"라고만 말할 수 있었다. 이제 같은 경우가 200 + convertible:false 로 온다.
 * - `issues[]` 는 **사람이 읽는 한국어 한 줄들**이고, 그림 요소 컨테이너가 배열이 아닐 때
 *   그 이름을 그대로 적어 준다 — 예: `'curves 이 배열이 아님'`.
 *   대상 키는 `curves | vlines | hlines | segments | circles | polygons | points` 일곱이다
 *   (`labels` 는 종전대로 관용 — issue 로 잡지 않는다).
 *   **그대로 화면에 내보낸다.** 여기서 "변환할 수 없습니다" 한마디로 뭉개면, 검수자는 그림을
 *   손봐서 다시 시도할지 포기할지 판단할 근거를 잃는다.
 * - expr 의 파이썬식 거듭제곱 `x**2` 는 서버가 `x^2` 로 **정규화해 받아들인다**(E1). 즉 그것만으로
 *   convertible:false 가 되지는 않는다. 이전에는 `9**9**9` 같은 식이 워커를 5분 정지시켰다.
 * - 12MB·40MP 초과나 비이미지는 400. 그 한도가 원본 바이트 기준인지 base64 기준인지는 계약에
 *   없어 여기서 미리 재지 않는다(잘못 재면 멀쩡한 이미지를 막는다).
 * - 채택하면 본문 Lexical 의 해당 image 노드 src 를 이 svg 로 갈아 끼운 뒤 updateProblem 으로 저장.
 */
// userPrompt(선택) — 「점선은 무시하라」처럼 이 그림에만 통하는 지시. 비면 키를 싣지 않는다.
// **수용 확정** — BE-20260829-reply-fe-0828.md §4-1(9곳 전부 수용·dev 배포) · §4-2: 상한 500자
// (공백 제거 후 기준, 초과 400) · 빈 문자열·공백만은 키를 안 보낸 것과 같다 · 프롬프트 말미의
// `[추가 지시 — 요청자]` 블록으로 붙고 옵션·규칙과 충돌하면 옵션·규칙이 이긴다.
// 단 이 엔드포인트만 **저장이 없다**(§4-1 표) — 요청 레코드가 없어 지시가 어디에도 남지 않는다.
// 결과를 PATCH 로 채택할 때 본문 이력에 남는 것은 `source:convert` 뿐이다.
// 프로젝트 기본 지시(OCR `default_prompt`)도 여기엔 적용되지 않는다(§5-4 — 그림 지시는 성격이 달라 섞지 않는다).
// mode(선택) — 계약 §9-4 「변환 모드」(2026-08-31). 값과 뜻은 figureApi.FIGURE_MODES 에 한 벌로 있다.
// **기본(faithful)이면 키를 싣지 않는다** — 아직 mode 를 모르는 배포본에 400 을 맞을 자리를
// 만들지 않는다. 이 경로에서 낼 수 있는 것은 faithful·schematic 둘이다:
// trace 는 계약이 「은행 **재요청**만」이라 못 박아 이 엔드포인트에 없다.
export async function convertFigspec(imageDataUri, getAccessToken, { userPrompt, mode } = {}) {
  const payload = { image: imageDataUri };
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  const modeValue = figureModePayload(mode);
  if (modeValue) payload.mode = modeValue;
  return requestJsonWithStatus(
    '/api/v2/problems/figspec-convert',
    { method: 'POST', payload },
    getAccessToken,
    '이미지 변환에 실패했습니다.',
  );
}

// ════ 라벨 (/api/v2/labels) — 문제 편집기의 [라벨] 칸 ════
//
// 계약: docs/exam-builder-v2/BE-20260830-reply-ocr-repromote-history-close.md §1 3-1 · 요청서
//       docs/problem-hub-v2/FE-20260913-concept-difficulty-direct-edit.md §1-6(어긋난 곳 셋).
// - 붙이기는 **사전 번호(label_uuid)로만** 된다. 사전에 없는 이름은 `POST /api/v2/labels` 로 먼저 만든다.
// - 떼기는 `DELETE …?label_uuid=` — 한 번에 하나.
// - 붙이기·떼기는 v1 문자열(problems.labels)을 고쳐 **문제의 updated_at 을 올린다**(kyhub Problems.updated_at
//   onupdate). 그래서 편집기는 문제 PATCH 를 **먼저** 보내고 라벨을 뒤에 쓴 다음 상세를 다시 읽는다 —
//   거꾸로 하면 PATCH 가 자기 라벨 쓰기 때문에 409 stale 을 맞는다.
// - 권한 user_required(교사 통과). 바뀐 것은 문제 이력(field labels)에 남는다.
// - 되돌릴 수 없는 것 하나: **사전에 새로 만든 라벨은 지우는 API 가 없다.**

/**
 * 이 문제에 붙은 라벨(사전 번호 포함). 279bbed 부터 상세(GET /problems/{id})가 같은 목록을 `labels` 로 싣는다
 * (회신 BE-20260913 §1) — 편집기는 그것을 쓰고, **이것은 상세에 `labels` 가 없는 옛 서버에서만** 부른다.
 */
export async function fetchProblemLabels(problemId, getAccessToken) {
  return getJsonWithStatus(`/api/v2/labels/problems/${problemId}`, getAccessToken, '라벨을 불러오지 못했습니다.');
}

/** 라벨 사전 검색 — 이름 부분 일치(ILIKE). 항목: label_uuid · label_name · slug · label_type · problem_count. */
export async function searchLabels(q, getAccessToken, { perPage = 8 } = {}) {
  const params = new URLSearchParams({
    q: String(q ?? '').trim(),
    per_page: String(Math.min(Math.max(Number(perPage) || 8, 5), 100)), // 서버가 5~100 으로 자른다
  });
  const d = await getJsonWithStatus(`/api/v2/labels?${params}`, getAccessToken, '라벨 사전을 불러오지 못했습니다.');
  return Array.isArray(d?.items) ? d.items : [];
}

export async function createLabel(labelName, getAccessToken) {
  return requestJsonWithStatus('/api/v2/labels', { method: 'POST', payload: { label_name: labelName } }, getAccessToken, '라벨을 만들지 못했습니다.');
}

export async function attachLabels(problemId, labelUuids, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/labels/problems/${problemId}`,
    { method: 'POST', payload: { label_uuids: labelUuids } },
    getAccessToken,
    '라벨을 붙이지 못했습니다.',
  );
}

export async function detachLabel(problemId, labelUuid, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/labels/problems/${problemId}?label_uuid=${encodeURIComponent(labelUuid)}`,
    { method: 'DELETE' },
    getAccessToken,
    '라벨을 떼지 못했습니다.',
  );
}

/**
 * 이름으로 사전 라벨을 찾는다 — slug 가 같은 항목. 사전 생성이 「이미 있다」(409)로 답했는데 **기존 라벨이 실려 오지
 * 않았을 때만**(279bbed 전 서버 — 요청서 §2-2) 쓴다. 검색이 부분 일치라, 문장부호만 다른 표기
 * (「쎈!」 → 「쎈」)도 걸리게 문장부호를 뺀 이름으로 한 번 더 찾는다.
 */
async function findLabelBySlug(labelName, getAccessToken) {
  const want = labelSlug(labelName);
  const hit = (list) => list.find((it) => (it.slug || labelSlug(it.label_name)) === want) || null;
  const first = hit(await searchLabels(labelName, getAccessToken, { perPage: 20 }));
  if (first) return first;
  const bare = String(labelName ?? '').replace(/[^\p{L}\p{N}_\s]/gu, '').trim();
  if (!bare || bare === String(labelName ?? '').trim()) return null;
  return hit(await searchLabels(bare, getAccessToken, { perPage: 20 }));
}

/**
 * 라벨 초안을 서버에 쓴다 — **떼기 → 만들기 → 붙이기(한 번)** 순서.
 * 떼기를 먼저 하는 이유: 문자열 합이 255자를 넘으면 붙이기가 409 인데, 떼는 것이 먼저 자리를 비운다.
 *
 * plan = labelEdit.labelPlan() 의 { removed: [{label_uuid,label_name}], added: [{label_uuid|null,label_name}] }.
 * 반환 { detached, created, attached } · 401 이면 AUTH_REDIRECT.
 * 한 단계라도 실패하면 거기서 멈추고 Error 에 `step`('detach'|'create'|'attach')·`label`(이름)을 실어 던진다 —
 * 이미 된 것은 서버에 남는다. 부르는 쪽은 성공·실패 모두 상세와 라벨을 다시 읽는다(labelPlan 이 남은 것만 다시 센다).
 */
export async function applyLabelPlan(problemId, plan, getAccessToken) {
  const tag = (e, step, label) => Object.assign(e instanceof Error ? e : new Error(String(e)), { step, label });
  let detached = 0;
  let created = 0;
  for (const l of plan?.removed || []) {
    let r;
    try {
      // eslint-disable-next-line no-await-in-loop -- 서버가 한 번에 하나씩만 뗀다
      r = await detachLabel(problemId, l.label_uuid, getAccessToken);
    } catch (e) { throw tag(e, 'detach', l.label_name); }
    if (r === AUTH_REDIRECT) return AUTH_REDIRECT;
    detached += 1;
  }
  const uuids = [];
  for (const a of plan?.added || []) {
    if (a.label_uuid) { uuids.push(a.label_uuid); continue; } // eslint-disable-line no-continue
    let c;
    try {
      // eslint-disable-next-line no-await-in-loop -- 만들 때마다 번호를 받아야 붙일 수 있다
      c = await createLabel(a.label_name, getAccessToken);
    } catch (e) {
      if (e?.httpStatus !== 409) throw tag(e, 'create', a.label_name);
      // 279bbed 부터 이름 중복 409 에 기존 라벨이 실려 온다(회신 BE-20260913 §1) — 있으면 그 번호를 바로 쓴다.
      // 409 가 늘 data 를 싣는다고 가정하지 않는다(붙이기의 255자 409 는 data: null — 회신 §6-2).
      if (e?.data?.label_uuid) {
        uuids.push(e.data.label_uuid);
        continue; // eslint-disable-line no-continue
      }
      // 옛 서버 — 검색해 slug 가 같은 항목을 찾는다
      let found;
      try {
        // eslint-disable-next-line no-await-in-loop
        found = await findLabelBySlug(a.label_name, getAccessToken);
      } catch (e2) { throw tag(e2, 'create', a.label_name); }
      if (!found) {
        throw tag(new Error('사전에 같은 라벨이 있다는 답을 받았지만 찾지 못했습니다 — 표기를 바꿔 다시 넣어 보세요.'), 'create', a.label_name);
      }
      uuids.push(found.label_uuid);
      continue; // eslint-disable-line no-continue
    }
    if (c === AUTH_REDIRECT) return AUTH_REDIRECT;
    uuids.push(c.label_uuid);
    created += 1;
  }
  const unique = [...new Set(uuids.filter(Boolean))];
  if (unique.length > 0) {
    let r;
    try {
      r = await attachLabels(problemId, unique, getAccessToken);
    } catch (e) { throw tag(e, 'attach', null); }
    if (r === AUTH_REDIRECT) return AUTH_REDIRECT;
  }
  return { detached, created, attached: unique.length };
}
